import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Article as NewspaperIcon, Event as EventIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useNews } from '../hooks/useNews';
import { useEvents } from '../hooks/useEvents';
import { useEventPhotos } from './events/useEventPhotos';
import { useAuth } from '../contexts/AuthContext';
import { MEMBER_TYPES } from '../types/User';
import { Event } from '../types/Event';
import { adminStyles } from '../theme/theme';
import ResponsiveTitle from '../components/ResponsiveTitle';
import EventDebriefDialog from './events/EventDebriefDialog';
import LocationPopUpDialog from '../components/LocationPopUpDialog';

const ITEMS_PER_PAGE = 5;

const formatNewsDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatEventDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

type CombinedItem =
  | { type: 'news'; data: any; date: Date }
  | { type: 'event'; data: Event; date: Date };

const News = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { news, newsLoading } = useNews(null); // null pour tout récupérer
  const { pastEvents, loading: eventsLoading } = useEvents();
  const eventPhotos = useEventPhotos(pastEvents);

  const [page, setPage] = useState(1);
  const [selectedPastEventId, setSelectedPastEventId] = useState<string | null>(null);
  const [openDebriefDialog, setOpenDebriefDialog] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePastEventClick = (eventId: string) => {
    setSelectedPastEventId(eventId);
    setOpenDebriefDialog(true);
  };

  const handleLocationClick = (locationId: string) => {
    setSelectedLocationId(locationId);
    setIsLocationDialogOpen(true);
  };

  // Combiner et trier les actualités et les événements passés
  const combinedItems: CombinedItem[] = useMemo(() => {
    const newsItems: CombinedItem[] = news.map(item => ({
      type: 'news',
      data: item,
      date: new Date(item.publishDate)
    }));

    const eventItems: CombinedItem[] = pastEvents.map(event => ({
      type: 'event',
      data: event,
      date: new Date(event.endDate)
    }));

    return [...newsItems, ...eventItems].sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [news, pastEvents]);

  // Calculer les items à afficher pour la page courante
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = combinedItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(combinedItems.length / ITEMS_PER_PAGE);

  const loading = newsLoading || eventsLoading;

  return (
    <PageLayout>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 4,
          px: 3,
          py: 1,
          color: 'white'
        }}>
          <NewspaperIcon />
          <ResponsiveTitle variant="h4">
            Toutes les actualités
          </ResponsiveTitle>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : combinedItems.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {currentItems.map((item, index) => {
            if (item.type === 'news') {
              const newsItem = item.data;
              return (
                <Card key={`news-${newsItem.id}`} sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)', '&:hover': { boxShadow: 'none' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0, py: 0 }}>
                      <ResponsiveTitle
                        variant="h5"
                        component="div"
                        minFontSize={18}
                        maxFontSize={32}
                        style={{
                          color: '#fff',
                          fontWeight: 'bold',
                          lineHeight: 0.8,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        sx={{ mr: 1.5 }}
                        dangerouslySetInnerHTML={{ __html: newsItem.title }}
                      />

                      <Typography
                        variant="body2"
                        color="rgba(150, 150, 150, 0.7)"
                        sx={{
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'inline' },
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}
                      >
                        Publié le {formatNewsDate(newsItem.publishDate)} par {newsItem.author}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      {user?.role === MEMBER_TYPES.ADMIN && (
                        <Tooltip title="Modifier l'actualité">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/news/edit/${newsItem.id}`)}
                            sx={{
                              ml: 1,
                              ...adminStyles.glassButton,
                              '&:hover': {
                                ...adminStyles.glassButton['&:hover'],
                                bgcolor: 'primary.main'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: { xs: 'block', sm: 'none' }
                      }}
                    >
                      Publié le {formatNewsDate(newsItem.publishDate)} par {newsItem.author}
                    </Typography>

                    <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ color: '#ddd', my: 2 }}
                      dangerouslySetInnerHTML={{ __html: newsItem.content }}
                    />

                    {newsItem.imageUrl && (
                      <Box
                        sx={{
                          mb: 3,
                          mt: 4,
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mx: { xs: 1, sm: 2, md: 3 }
                        }}
                      >
                        <Box
                          component="img"
                          src={newsItem.imageUrl}
                          alt={newsItem.title}
                          loading="lazy"
                          sx={{
                            width: 'auto',
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '800px',
                            objectFit: 'contain',
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            } else {
              const eventItem = item.data;
              const eventImages = eventPhotos[eventItem.id || ''] || [];
              const hasPhotos = eventImages.length > 0;

              return (
                <Card key={`event-${eventItem.id}`} sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)', '&:hover': { boxShadow: 'none' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0, py: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#fff', mr: 2 }}>
                        <EventIcon sx={{ mr: 1 }} />
                        <Typography variant="overline" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1 }}>
                          Événement passé
                        </Typography>
                      </Box>

                      <ResponsiveTitle
                        variant="h5"
                        component="div"
                        minFontSize={18}
                        maxFontSize={32}
                        style={{
                          color: '#fff',
                          fontWeight: 'bold',
                          lineHeight: 0.8,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        sx={{ mr: 1.5 }}
                      >
                        {eventItem.title}
                      </ResponsiveTitle>

                      <Typography
                        variant="body2"
                        color="rgba(150, 150, 150, 0.7)"
                        sx={{
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'inline' },
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}
                      >
                        {formatEventDate(eventItem.startDate)}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      {user?.role === MEMBER_TYPES.ADMIN && (
                        <Tooltip title="Voir le debrief / Gérer les photos">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (eventItem.id) {
                                handlePastEventClick(eventItem.id);
                              }
                            }}
                            sx={{
                              ml: 1,
                              ...adminStyles.glassButton,
                              '&:hover': {
                                ...adminStyles.glassButton['&:hover'],
                                bgcolor: 'primary.main'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: { xs: 'block', sm: 'none' }
                      }}
                    >
                      {formatEventDate(eventItem.startDate)}
                    </Typography>

                    <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                    {eventItem.debrief?.content ? (
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{
                          color: '#ddd',
                          my: 2,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {eventItem.debrief.content}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{ color: '#ddd', my: 2, fontStyle: 'italic' }}
                      >
                        L'événement est terminé ! Retrouvez les photos et le résumé de cette soirée.
                      </Typography>
                    )}

                    {/* Grille de photos (seulement si disponibles) */}
                    {hasPhotos && (
                      <Box sx={{
                        mt: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'flex-start'
                      }}>
                        {eventImages.slice(0, 4).map((imgUrl, index) => (
                          <Box
                            key={index}
                            onClick={() => {
                              if (eventItem.id) {
                                handlePastEventClick(eventItem.id);
                              }
                            }}
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              maxWidth: '100%',
                              flexShrink: 1
                            }}
                          >
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`Photo événement ${index + 1}`}
                              sx={{
                                display: 'block',
                                maxWidth: '100%',
                                width: 'auto',
                                height: { xs: 120, sm: 160, md: 200 },
                                objectFit: 'cover',
                                borderRadius: 1,
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'scale(1.02)' }
                              }}
                            />
                            {/* Si c'est la 4ème image et qu'il y en a plus, afficher un overlay */}
                            {index === 3 && eventImages.length > 4 && (
                              <Box sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                zIndex: 2
                              }}>
                                +{eventImages.length - 4} photos
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }
          })}

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'white',
                  fontSize: '1.1rem'
                },
                '& .Mui-selected': {
                  backgroundColor: 'primary.main',
                  fontWeight: 'bold'
                }
              }}
            />
          </Box>
        </Box>
      ) : (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: '#aaa' }}>
          Aucune actualité disponible.
        </Typography>
      )}

      <LocationPopUpDialog
        open={isLocationDialogOpen}
        onClose={() => setIsLocationDialogOpen(false)}
        locationId={selectedLocationId}
      />

      <EventDebriefDialog
        open={openDebriefDialog}
        onClose={() => setOpenDebriefDialog(false)}
        eventId={selectedPastEventId}
        onEventUpdated={() => { }}
        onEdit={() => navigate('/events')}
        onDelete={() => navigate('/events')}
        onLocationClick={handleLocationClick}
      />
    </PageLayout>
  );
};

export default News;
